import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  callAdjustCredits,
  callClearReportAI,
  callDeleteReport,
  callGetReport,
  callListPurchases,
  callListUserReports,
  callSetCredits,
  type AdminAccount,
  type AdminPurchase,
  type AdminReport,
  type AdminReportFull,
  type AdminSecurityEvent,
} from '../firebase';
import { JsonViewer } from '../components/JsonViewer';
import { useSort, type SortValue } from '../sorting';

interface State {
  credits: number;
  fullReportsCount: number;
  reports: AdminReport[];
  account: AdminAccount;
  securityEvents: AdminSecurityEvent[];
}

function eventLabel(type: string): string {
  if (type === 'source_inspection') return 'Inspección de código';
  if (type === 'report_harvest') return 'Cosecha de informe';
  return type;
}

function fmtDate(ms: number | null): string {
  if (ms === null) return '—';
  return new Date(ms).toLocaleString('es-ES');
}

function YesNo({ value }: { value: boolean }) {
  if (value) {
    return <span className="badge" style={{ background: '#1f6f3a', color: '#fff', borderColor: '#1f6f3a' }}>Sí</span>;
  }
  return <span className="muted">No</span>;
}

type ReportSortKey = 'id' | 'source' | 'ai' | 'created';
type PurchaseSortKey = 'at' | 'product' | 'credits' | 'id';

function reportSortValue(r: AdminReport, key: ReportSortKey): SortValue {
  switch (key) {
    case 'id':
      return r.id.toLowerCase();
    case 'source':
      return r.source;
    case 'ai':
      return r.hasAI ? 1 : 0;
    case 'created':
      return r.createdAt ?? null;
  }
}

function purchaseSortValue(p: AdminPurchase, key: PurchaseSortKey): SortValue {
  switch (key) {
    case 'at':
      return p.at ?? null;
    case 'product':
      return p.productId?.toLowerCase() ?? null;
    case 'credits':
      return p.credits ?? null;
    case 'id':
      return p.id.toLowerCase();
  }
}

export function UserDetail() {
  const { uid } = useParams<{ uid: string }>();
  const [state, setState] = useState<State | null>(null);
  const [purchases, setPurchases] = useState<AdminPurchase[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [adjustValue, setAdjustValue] = useState<string>('1');
  const [setValue, setSetValue] = useState<string>('');
  const [busyCredits, setBusyCredits] = useState(false);
  const [viewer, setViewer] = useState<{ reportId: string; data: AdminReportFull | null; loading: boolean; error: string | null } | null>(null);
  const [busyAi, setBusyAi] = useState<string | null>(null);

  const reload = useCallback(async () => {
    if (!uid) return;
    setLoading(true);
    setErr(null);
    try {
      const [reportsRes, purchasesRes] = await Promise.all([
        callListUserReports({ uid }),
        callListPurchases({ uid }).catch(() => null),
      ]);
      setState(reportsRes.data);
      setSetValue(String(reportsRes.data.credits));
      setPurchases(purchasesRes?.data.purchases ?? []);
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setLoading(false);
    }
  }, [uid]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const {
    sorted: sortedReports,
    toggle: toggleReport,
    arrow: arrowReport,
  } = useSort<AdminReport, ReportSortKey>(state?.reports ?? [], reportSortValue);
  const {
    sorted: sortedPurchases,
    toggle: togglePurchase,
    arrow: arrowPurchase,
  } = useSort<AdminPurchase, PurchaseSortKey>(purchases ?? [], purchaseSortValue);

  if (!uid) return <p>uid faltante</p>;

  async function adjust(delta: number) {
    if (!uid) return;
    if (!Number.isInteger(delta)) return;
    setBusyCredits(true);
    setErr(null);
    try {
      await callAdjustCredits({ uid, delta });
      await reload();
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusyCredits(false);
    }
  }

  async function setAbs() {
    if (!uid) return;
    const n = Number(setValue);
    if (!Number.isInteger(n) || n < 0) {
      setErr('El saldo debe ser un entero ≥ 0.');
      return;
    }
    setBusyCredits(true);
    setErr(null);
    try {
      await callSetCredits({ uid, credits: n });
      await reload();
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusyCredits(false);
    }
  }

  async function openViewer(reportId: string) {
    if (!uid) return;
    setViewer({ reportId, data: null, loading: true, error: null });
    try {
      const res = await callGetReport({ uid, reportId });
      setViewer({ reportId, data: res.data, loading: false, error: null });
    } catch (e) {
      setViewer({ reportId, data: null, loading: false, error: (e as Error).message });
    }
  }

  async function clearAi(reportId: string) {
    if (!uid) return;
    if (!confirm(`¿Limpiar el caché de IA del informe ${reportId}?\n\nSe regenerará la próxima vez que el usuario abra el informe.`)) return;
    setBusyAi(reportId);
    setErr(null);
    try {
      const res = await callClearReportAI({ uid, reportId });
      alert(`Caché IA limpiado (${res.data.cleared} doc${res.data.cleared === 1 ? '' : 's'}).`);
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusyAi(null);
    }
  }

  async function removeReport(reportId: string) {
    if (!uid) return;
    if (!confirm(`¿Borrar el informe ${reportId}?\n\nNo se devuelve el crédito al usuario.`)) return;
    setErr(null);
    try {
      await callDeleteReport({ uid, reportId });
      // Actualización optimista para no recargar todo
      setState((s) => (s ? { ...s, reports: s.reports.filter((r) => r.id !== reportId) } : s));
    } catch (e) {
      setErr((e as Error).message);
      await reload();
    }
  }

  return (
    <div>
      <div className="row between" style={{ marginBottom: 16 }}>
        <h2 style={{ margin: 0 }}>
          <Link to="/users" className="muted">
            ← Usuarios
          </Link>
          {' / '}
          <span style={{ fontFamily: 'ui-monospace, monospace', fontSize: 14 }}>{uid}</span>
        </h2>
        <button onClick={reload} disabled={loading}>
          {loading ? 'Cargando…' : 'Refrescar'}
        </button>
      </div>

      {err && <div className="error">{err}</div>}

      {state && (
        <>
          <div className="panel">
            <div className="row between" style={{ marginBottom: 8 }}>
              <h3 style={{ margin: 0 }}>Cuenta y seguridad</h3>
              {state.account.suspicious ? (
                <span className="badge" style={{ background: '#b3261e', color: '#fff', borderColor: '#b3261e' }}>
                  ⚠ SOSPECHOSO
                </span>
              ) : (
                <span className="badge">Sin marca</span>
              )}
            </div>
            <dl className="kv">
              <dt>Email</dt>
              <dd>{state.account.email ?? '—'}</dd>
              <dt>Sospechoso</dt>
              <dd><YesNo value={state.account.suspicious} /></dd>
              <dt>Motivo</dt>
              <dd>{state.account.suspiciousReason ?? '—'}</dd>
              <dt>Marcado sospechoso</dt>
              <dd className="muted">{fmtDate(state.account.suspiciousAt)}</dd>
              <dt>IP de registro</dt>
              <dd style={{ fontFamily: 'ui-monospace, monospace' }}>{state.account.regIp ?? '—'}</dd>
              <dt>Fecha de la IP</dt>
              <dd className="muted">{fmtDate(state.account.regIpAt)}</dd>
              <dt>Fingerprint de registro</dt>
              <dd style={{ fontFamily: 'ui-monospace, monospace', wordBreak: 'break-all' }}>{state.account.regFp ?? '—'}</dd>
              <dt>Fecha del fingerprint</dt>
              <dd className="muted">{fmtDate(state.account.regFpAt)}</dd>
              <dt>Origen de registro</dt>
              <dd style={{ fontFamily: 'ui-monospace, monospace', wordBreak: 'break-all' }}>{state.account.regOrigin ?? '—'}</dd>
              <dt>App Check (attested)</dt>
              <dd><YesNo value={state.account.appAttested} /></dd>
              <dt>Crédito gratis concedido</dt>
              <dd><YesNo value={state.account.freeCreditGranted} /></dd>
              <dt>Opt-in marketing</dt>
              <dd><YesNo value={state.account.marketingConsent} /> <span className="muted">{fmtDate(state.account.marketingConsentAt)}</span></dd>
              <dt>Creado</dt>
              <dd className="muted">{fmtDate(state.account.createdAt)}</dd>
              <dt>Último login</dt>
              <dd className="muted">{fmtDate(state.account.lastLoginAt)}</dd>
            </dl>
          </div>

          <div className="panel" style={{ padding: 0 }}>
            <div style={{ padding: 16, paddingBottom: 8 }}>
              <h3 style={{ margin: 0 }}>Eventos de seguridad ({state.securityEvents.length})</h3>
            </div>
            <table className="table">
              <thead>
                <tr>
                  <th>Fecha</th>
                  <th>Tipo</th>
                  <th>IP</th>
                  <th>Detalle</th>
                </tr>
              </thead>
              <tbody>
                {state.securityEvents.map((ev) => (
                  <tr key={ev.id}>
                    <td className="muted">{fmtDate(ev.createdAt)}</td>
                    <td>
                      <span
                        className="badge"
                        style={{ background: '#8a4b00', color: '#fff', borderColor: '#8a4b00' }}
                      >
                        {eventLabel(ev.type)}
                      </span>
                    </td>
                    <td style={{ fontFamily: 'ui-monospace, monospace' }}>{ev.ip ?? '—'}</td>
                    <td className="muted" style={{ fontFamily: 'ui-monospace, monospace', fontSize: 12, wordBreak: 'break-all' }}>
                      {Object.keys(ev.meta).length > 0 ? JSON.stringify(ev.meta) : '—'}
                    </td>
                  </tr>
                ))}
                {state.securityEvents.length === 0 && (
                  <tr>
                    <td colSpan={4} className="muted" style={{ textAlign: 'center', padding: 32 }}>
                      Sin eventos de seguridad para este usuario.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="panel">
            <h3 style={{ marginTop: 0 }}>Créditos</h3>
            <dl className="kv" style={{ marginBottom: 16 }}>
              <dt>Saldo actual</dt>
              <dd>
                <strong style={{ fontSize: 18 }}>{state.credits}</strong>
              </dd>
              <dt>Total informes históricos</dt>
              <dd>{state.fullReportsCount}</dd>
            </dl>

            <div className="credits-controls">
              <button onClick={() => adjust(-1)} disabled={busyCredits || state.credits <= 0}>
                −1
              </button>
              <button onClick={() => adjust(1)} disabled={busyCredits}>
                +1
              </button>
              <span className="muted">|</span>
              <span className="muted">Ajustar:</span>
              <input
                type="number"
                value={adjustValue}
                onChange={(e) => setAdjustValue(e.target.value)}
                disabled={busyCredits}
              />
              <button
                onClick={() => adjust(Number(adjustValue))}
                disabled={busyCredits || !Number.isInteger(Number(adjustValue))}
              >
                Sumar Δ
              </button>
              <button
                onClick={() => adjust(-Number(adjustValue))}
                disabled={busyCredits || !Number.isInteger(Number(adjustValue))}
              >
                Restar Δ
              </button>
              <span className="muted">|</span>
              <span className="muted">Fijar:</span>
              <input
                type="number"
                value={setValue}
                onChange={(e) => setSetValue(e.target.value)}
                disabled={busyCredits}
              />
              <button className="primary" onClick={setAbs} disabled={busyCredits}>
                Fijar saldo
              </button>
            </div>
          </div>

          <div className="panel" style={{ padding: 0 }}>
            <div style={{ padding: 16, paddingBottom: 8 }}>
              <h3 style={{ margin: 0 }}>Compras ({purchases?.length ?? 0})</h3>
            </div>
            <table className="table">
              <thead>
                <tr>
                  <th className="sortable" onClick={() => togglePurchase('at')}>Fecha{arrowPurchase('at')}</th>
                  <th className="sortable" onClick={() => togglePurchase('product')}>Pack (SKU){arrowPurchase('product')}</th>
                  <th className="sortable num" onClick={() => togglePurchase('credits')}>Créditos{arrowPurchase('credits')}</th>
                  <th className="sortable" onClick={() => togglePurchase('id')}>Transaction id{arrowPurchase('id')}</th>
                </tr>
              </thead>
              <tbody>
                {sortedPurchases.map((p) => (
                  <tr key={p.id}>
                    <td className="muted">{p.at ? new Date(p.at).toLocaleString('es-ES') : '—'}</td>
                    <td>
                      <span className="badge">{p.productId || '—'}</span>
                    </td>
                    <td className="num">{p.credits ?? '—'}</td>
                    <td className="report-id">{p.id}</td>
                  </tr>
                ))}
                {(purchases?.length ?? 0) === 0 && (
                  <tr>
                    <td colSpan={4} className="muted" style={{ textAlign: 'center', padding: 32 }}>
                      Este usuario no ha realizado compras.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="panel" style={{ padding: 0 }}>
            <div style={{ padding: 16, paddingBottom: 8 }}>
              <h3 style={{ margin: 0 }}>Informes ({state.reports.length})</h3>
            </div>
            <table className="table">
              <thead>
                <tr>
                  <th className="sortable" onClick={() => toggleReport('id')}>ID{arrowReport('id')}</th>
                  <th className="sortable" onClick={() => toggleReport('source')}>Origen{arrowReport('source')}</th>
                  <th className="sortable" onClick={() => toggleReport('ai')}>IA{arrowReport('ai')}</th>
                  <th className="sortable" onClick={() => toggleReport('created')}>Creado{arrowReport('created')}</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {sortedReports.map((r) => (
                  <tr key={r.id}>
                    <td className="report-id">{r.id}</td>
                    <td>
                      <span className="badge">{r.source === 'sub' ? 'subcollection' : 'map (legacy)'}</span>
                    </td>
                    <td>
                      {r.hasAI ? (
                        <span
                          className="badge"
                          title="Tiene caché de análisis IA (ai/latest)"
                          style={{ background: '#1f6f3a', color: '#fff', borderColor: '#1f6f3a' }}
                        >
                          ● IA
                        </span>
                      ) : (
                        <span className="muted" title="Sin caché de IA">—</span>
                      )}
                    </td>
                    <td className="muted">{r.createdAt ? new Date(r.createdAt).toLocaleString('es-ES') : '—'}</td>
                    <td>
                      <div className="row" style={{ justifyContent: 'flex-end' }}>
                        <button onClick={() => openViewer(r.id)}>Ver JSON</button>
                        <button
                          onClick={() => clearAi(r.id)}
                          disabled={busyAi === r.id || !r.hasAI}
                        >
                          {busyAi === r.id ? 'Limpiando…' : 'Limpiar IA'}
                        </button>
                        <button className="danger" onClick={() => removeReport(r.id)}>
                          Borrar
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {state.reports.length === 0 && (
                  <tr>
                    <td colSpan={5} className="muted" style={{ textAlign: 'center', padding: 32 }}>
                      Este usuario aún no ha generado informes.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}

      {viewer && (
        <JsonViewer
          title={`reports/${viewer.reportId}`}
          data={viewer.data}
          loading={viewer.loading}
          error={viewer.error}
          onClose={() => setViewer(null)}
        />
      )}
    </div>
  );
}
